import { useState } from "react";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Send, ChevronDown, Mail, HelpCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { APP_NAME, SUPPORT_EMAIL } from "@/lib/constants";
import { SEO } from "@/components/SEO";
import { useSystemSettings } from "@/hooks/use-system-settings";

const fallbackFaqs = [
  { id: '1', q: `מה זה ${APP_NAME}?`, a: `${APP_NAME} היא פלטפורמה המאפשרת ליוצרים לפרסם מערכות ופתרונות דיגיטליים חכמים, ולמשתמשים לגלות אותם. אנחנו מחברים בין טכנולוגיה לאנשים.` },
  { id: '2', q: "האם פרסום מערכת עולה כסף?", a: `לא. פרסום מערכת ב-${APP_NAME} הוא בחינם לחלוטין. מטרתנו היא לתת במה ליוצרים.` },
  { id: '3', q: "למה המערכת שלי לא אושרה?", a: "כל מערכת עוברת בדיקה ידנית כדי לוודא שהיא עומדת בסטנדרטים שלנו. סיבות נפוצות לדחייה: מידע חסר, תיאור לא ברור, או תוכן שאינו הולם. תקבלו התראה עם הסיבה המדויקת." },
  { id: '4', q: "איך עובד הדירוג?", a: "כל משתמש רשום יכול לדרג מערכת פעם אחת. ניתן לערוך את הדירוג אחת ל-24 שעות. לא ניתן לדרג מערכת שיצרתם בעצמכם. מטרת הדירוגים היא ליצור תמונת מצב אמינה ושקופה." },
  { id: '5', q: "איך מוחקים חשבון?", a: "ניתן לפנות אלינו דרך טופס יצירת הקשר בנושא \"מחיקת חשבון\". המידע האישי שלכם יימחק תוך 30 יום עסקים בהתאם למדיניות הפרטיות." },
  { id: '6', q: "איך אני יכול לערוך את המערכת שפרסמתי?", a: "היכנסו ללוח הבקרה (ניהול מערכות), ולחצו על אייקון העריכה. שימו לב: עריכה מהותית עשויה להחזיר את המערכת לסטטוס \"ממתין לאישור\" לבדיקה חוזרת." },
];

const Contact = () => {
  const { data: settings } = useSystemSettings();
  const faqItems = settings?.home_faq_items ? JSON.parse(settings.home_faq_items) : fallbackFaqs;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const currentUserId = session?.user?.id || null;

      const strip = (s: string) => s.replace(/<[^>]*>/g, '');
      const safeName = strip(String(name || '')).trim().slice(0, 200);
      const safeSubject = strip(String(subject || '')).trim().slice(0, 200);
      const safeMessage = strip(String(message || '')).trim().slice(0, 2000);

      const { error } = await supabase.from('user_activity_log' as any).insert({
        user_id: currentUserId,
        action: 'contact_form_submitted',
        metadata: { 
          name: safeName, 
          email: email.trim().toLowerCase(), 
          subject: safeSubject, 
          message: safeMessage,
          handled: false 
        }
      } as any);

      if (error) throw error;

      setSent(true);
      toast.success("ההודעה נשלחה בהצלחה!");
    } catch (err) {
      console.error("Contact form error:", err);
      toast.error("שגיאה בשליחת ההודעה. אנא נסה שוב מאוחר יותר.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <SEO 
        title="צור קשר" 
        description="שאלה, בעיה, רעיון לשיפור או סתם רוצים להגיד שלום — צוות FeeVho כאן בשבילכם. דברו איתנו ונחזור אליכם בהקדם." 
      />
      <div className="bg-white min-h-screen pb-20">

        {/* Hero */}
        <section className="pt-32 pb-16 border-b border-slate-100">
          <div className="container mx-auto px-6 lg:px-12 max-w-3xl text-center">
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-[1.1] mb-4">
              דברו איתנו
            </h1>
            <p className="text-lg text-slate-500 max-w-xl mx-auto">
              שאלה, בעיה, רעיון לשיפור או סתם רוצים להגיד שלום — נשמח לשמוע.
            </p>
          </div>
        </section>

        <div className="container mx-auto px-6 lg:px-12 max-w-4xl py-16">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">

            {/* Form — 3 cols */}
            <div className="lg:col-span-3">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center text-slate-700">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="font-bold text-lg text-slate-900">שלח הודעה</h2>
                  <p className="text-sm text-slate-500">נחזור אליך תוך יום עסקים אחד.</p>
                </div>
              </div>

              {sent ? (
                <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-10 text-center">
                  <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4 text-emerald-600">
                    <Send className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">ההודעה נשלחה!</h3>
                  <p className="text-slate-600">תודה שפנית אלינו. נחזור אליך בהקדם.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-slate-700">שם מלא</Label>
                      <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        maxLength={200}
                        placeholder="ישראל ישראלי"
                        className="h-11 rounded-xl border-slate-200 bg-slate-50/50 focus:bg-white focus:border-slate-900 transition-all shadow-none text-right"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-slate-700">אימייל</Label>
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        placeholder="name@example.com"
                        className="h-11 rounded-xl border-slate-200 bg-slate-50/50 focus:bg-white focus:border-slate-900 transition-all shadow-none text-left"
                        dir="ltr"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">נושא</Label>
                    <Select value={subject} onValueChange={setSubject} required>
                      <SelectTrigger className="h-11 rounded-xl border-slate-200 bg-slate-50/50 focus:bg-white transition-all shadow-none text-right" dir="rtl">
                        <SelectValue placeholder="בחר נושא..." />
                      </SelectTrigger>
                      <SelectContent className="rounded-xl" dir="rtl">
                        <SelectItem value="general" className="text-right">שאלה כללית</SelectItem>
                        <SelectItem value="publish" className="text-right">פרסום מערכת</SelectItem>
                        <SelectItem value="bug" className="text-right">דיווח על תקלה</SelectItem>
                        <SelectItem value="review" className="text-right">בעיה עם ביקורת / דירוג</SelectItem>
                        <SelectItem value="delete" className="text-right">מחיקת חשבון</SelectItem>
                        <SelectItem value="business" className="text-right">שיתוף פעולה</SelectItem>
                        <SelectItem value="other" className="text-right">אחר</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">הודעה</Label>
                    <Textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      required
                      maxLength={2000}
                      placeholder="ספר לנו במה נוכל לעזור..."
                      className="min-h-[140px] rounded-xl border-slate-200 bg-slate-50/50 focus:bg-white focus:border-slate-900 transition-all shadow-none resize-none text-right"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-11 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-medium shadow-none gap-2"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {loading ? "שולח..." : "שלח הודעה"}
                  </Button>
                </form>
              )}
            </div>

            {/* Info sidebar — 2 cols */}
            <div className="lg:col-span-2 space-y-8">
              <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
                <h3 className="font-bold text-slate-900 mb-4 text-sm">דרכי התקשרות</h3>
                <div className="space-y-4">
                  <div>
                    <div className="text-xs font-medium text-slate-400 mb-1">דוא"ל</div>
                    <a href={`mailto:${SUPPORT_EMAIL}`} className="text-sm text-blue-600 hover:underline">{SUPPORT_EMAIL}</a>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-slate-400 mb-1">WhatsApp</div>
                    <a href="https://wa.me/972525267239" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">
                      052-526-7239
                    </a>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-slate-400 mb-1">זמן מענה</div>
                    <div className="text-sm text-slate-700">עד יום עסקים אחד</div>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
                <h3 className="font-bold text-slate-900 mb-2 text-sm">לפני שפונים</h3>
                <p className="text-sm text-slate-500 leading-relaxed">
                  ייתכן שהתשובה כבר נמצאת בשאלות הנפוצות למטה. בדקו שם קודם — זה יחסוך לכם זמן.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <section className="py-20 bg-slate-50/50 border-t border-slate-100">
          <div className="container mx-auto px-6 lg:px-12 max-w-3xl">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-slate-700 border border-slate-200">
                <HelpCircle className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900 tracking-tight">שאלות נפוצות</h2>
                <p className="text-sm text-slate-500">תשובות מהירות לשאלות הכי נפוצות.</p>
              </div>
            </div>

            <div className="space-y-3">
              {faqItems.map((item: any) => (
                <div
                  key={item.id}
                  className="bg-white rounded-xl border border-slate-200 overflow-hidden transition-all"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === item.id ? null : item.id)}
                    className="w-full flex items-center justify-between p-5 text-start"
                  >
                    <span className="font-medium text-slate-900 pe-4">{item.q}</span>
                    <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform duration-200 ${openFaq === item.id ? 'rotate-180' : ''}`} />
                  </button>
                  <div className={`overflow-hidden transition-all duration-200 ${openFaq === item.id ? 'max-h-96' : 'max-h-0'}`}>
                    <div className="px-5 pb-5 pt-0">
                      <p className="text-slate-600 text-sm leading-relaxed">{item.a}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

      </div>
    </Layout>
  );
};

export default Contact;